import { useEffect, useRef, useState } from "react";
import { animate, motion } from "framer-motion";
import { BarChart3, Bell, Briefcase, Calendar, FileText, FolderKanban, Kanban, LayoutDashboard, Megaphone, Package, Receipt, Search, Timer, Users } from "lucide-react";

export const ease = [0.2, 0.8, 0.2, 1];
export const Em = ({ children }) => <em className="serif">{children}</em>;
export const aud0 = (v) => "$" + Math.round(v).toLocaleString("en-AU");
export const pct1 = (v) => v.toFixed(1) + "%";

const DESKTOP = "(min-width: 1024px)";
export function useIsDesktop() {
  const [d, setD] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => { const mq = window.matchMedia(DESKTOP); const h = (e) => setD(e.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, []);
  return d;
}

export function useClock(start = [7, 6, 12], run = true) {
  const [d, setD] = useState(() => new Date(2026, 5, 15, ...start));
  useEffect(() => { if (!run) return; const t = setInterval(() => setD((x) => new Date(x.getTime() + 1000)), 1000); return () => clearInterval(t); }, [run]);
  return d.toTimeString().slice(0, 8);
}

export function Num({ value, format = (v) => Math.round(v).toLocaleString("en-AU"), duration = 0.9, className = "" }) {
  const [txt, setTxt] = useState(() => format(value));
  const prev = useRef(value);
  useEffect(() => { const c = animate(prev.current, value, { duration, ease, onUpdate: (v) => setTxt(format(v)) }); prev.current = value; return () => c.stop(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span className={`tabular-nums ${className}`}>{txt}</span>;
}

const NAV = [["Dashboard", LayoutDashboard], ["CRM", Kanban], ["Quotes", FileText], ["Jobs", Briefcase], ["Projects", FolderKanban], ["Dispatch", Calendar], ["Clock In", Timer], ["Supplies", Package], ["Customers", Users], ["Invoices", Receipt], ["Social", Megaphone], ["Reports", BarChart3]];

export function Win({ crumb, active, children, className = "", side = true, right, testid }) {
  return (
    <div className={`pw ${className}`} data-testid={testid}>
      <div className="pw-bar">
        <span className="truncate font-semibold text-[#0A0A0B]">{crumb}</span>
        {right ?? (
          <span className="ml-auto flex shrink-0 items-center gap-2.5">
            <span className="hidden items-center gap-1.5 rounded-md border border-[#E7E5E4] px-2 py-1 text-[11px] text-[#A8A29E] sm:flex"><Search size={11} /> Search or jump to…<kbd className="ml-1 font-mono text-[9.5px]">⌘K</kbd></span>
            <Bell size={13} className="text-[#78716C]" />
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0A0A0B] font-mono text-[9px] font-bold text-white">SM</span>
          </span>
        )}
      </div>
      <div className="flex">
        {side && (
          <aside className="pw-side hidden py-2 md:block">
            {NAV.map(([l, I]) => <div key={l} className={`pw-nav ${l === active ? "on" : ""}`}><I />{l}</div>)}
            <div className="mx-4 mt-3 border-t border-[#E7E5E4] pt-2.5 text-[10.5px] leading-snug text-[#A8A29E]">Harbour City Plumbing<br />Sarah Mitchell · Admin</div>
          </aside>
        )}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

export function Device({ children, time = "7:02", className = "", testid }) {
  return (
    <div className={`device ${className}`} data-testid={testid}>
      <div className="device-screen">
        <div className="device-island" />
        <div className="device-status"><span>{time}</span><span className="flex items-center gap-1.5"><span className="font-mono text-[10px]">5G</span><span className="inline-block h-[9px] w-[18px] rounded-[3px] border border-[#0A0A0B] p-[1px]"><span className="block h-full w-[80%] rounded-[1px] bg-[#0A0A0B]" /></span></span></div>
        {children}
      </div>
    </div>
  );
}

export function Cursor({ at, click = false, show = true }) {
  return (
    <motion.svg className="cursor" viewBox="0 0 18 18" initial={false} animate={{ left: `${at[0]}%`, top: `${at[1]}%`, opacity: show ? 1 : 0, scale: click ? 0.8 : 1 }} transition={{ left: { duration: 0.75, ease }, top: { duration: 0.75, ease }, scale: { duration: 0.12 }, opacity: { duration: 0.3 } }} aria-hidden>
      <path d="M3 2l11 6.5-4.6 1.2L12 15l-2 1-2.6-5.3L3 14z" fill="#fff" stroke="#0A0A0B" strokeWidth="1.2" strokeLinejoin="round" />
    </motion.svg>
  );
}

export function Tap({ at, show }) {
  return <motion.span className="tapring" style={{ left: `${at[0]}%`, top: `${at[1]}%` }} initial={false} animate={show ? { opacity: [0, 1, 0], scale: [0.5, 1.2, 1.6] } : { opacity: 0, scale: 0.5 }} transition={{ duration: 0.9, ease }} aria-hidden />;
}

export function Pill({ tone = "zinc", children, className = "" }) { return <span className={`pw-pill ${tone} ${className}`}>{children}</span>; }

const TONES = ["#6B7280", "#4B5563", "#9CA3AF", "#78716C", "#374151", "#A8A29E"];
export function Photos({ n = 4, show = n, cols = "grid-cols-4", labels }) {
  return (
    <div className={`grid gap-1.5 ${cols}`}>
      {Array.from({ length: n }).map((_, i) => (
        <motion.div key={i} className="pw-photo aspect-square" data-n={labels?.[i] ?? `IMG_0${412 + i}`} style={{ background: TONES[i % TONES.length] }} initial={false} animate={{ opacity: i < show ? 1 : 0, scale: i < show ? 1 : 0.92 }} transition={{ duration: 0.35, ease }} />
      ))}
    </div>
  );
}

export function Signature({ draw, className = "h-14" }) {
  return (
    <svg viewBox="0 0 220 70" className={`w-full ${className}`} aria-hidden>
      <motion.path className="sig" d="M12 48 C 30 10, 42 12, 46 40 S 60 62, 74 34 S 96 14, 104 42 S 122 60, 138 30 C 150 12, 160 20, 168 40 S 190 52, 208 24" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: draw ? 1 : 0, opacity: draw ? 1 : 0 }} transition={{ duration: 1.1, ease }} />
    </svg>
  );
}

export function Typed({ text, on, speed = 22, className = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => { if (!on) { setN(0); return; } const t = setInterval(() => setN((x) => (x >= text.length ? x : x + 1)), speed); return () => clearInterval(t); }, [on, text, speed]);
  return <span className={`${on && n < text.length ? "typing" : ""} ${className}`}>{text.slice(0, n)}</span>;
}

export function KRow({ k, v, c = "", mono = false }) { return <div className="pw-row"><span className="text-[#78716C]">{k}</span><span className={`text-right font-medium ${mono ? "font-mono text-[11.5px]" : ""} ${c}`}>{v}</span></div>; }
